<template>
    <section class="tk-client-information">
        <section class="tk-card">
            <div class="tk-card-header">
                <h2>Person Details</h2>
                <q-btn
                    flat
                    dense
                    no-caps
                    color="primary"
                    label="Edit person"
                    @click="$emit('edit-person')"
                />
            </div>
            <div class="tk-field-grid">
                <div v-for="field in personDetailFields" :key="field.label" class="tk-field-row">
                    <span>{{ field.label }}</span>
                    <strong>{{ field.value }}</strong>
                </div>
            </div>

            <q-separator class="q-my-lg" />
            <h3 class="tk-information-subheading">Contact Details</h3>
            <div class="tk-contact-rows">
                <div v-for="(row, index) in contactRows" :key="`${row.label}-${index}`" class="tk-contact-row">
                    <span>{{ row.label }}:</span>
                    <a v-if="row.href" :href="row.href">{{ row.value }}</a>
                    <strong v-else>{{ row.value }}</strong>
                </div>
            </div>

            <q-separator class="q-my-lg" />
            <h3 class="tk-information-subheading">Whānau Contact</h3>
            <div v-if="emergencyContacts.length" class="tk-contact-rows">
                <div
                    v-for="(contact, index) in emergencyContacts"
                    :key="contact.id || index"
                    class="tk-emergency-contact"
                >
                    <div class="tk-contact-row">
                        <span>Name:</span>
                        <strong>{{ contact.name || "-" }}</strong>
                    </div>
                    <div class="tk-contact-row">
                        <span>Relationship:</span>
                        <strong>{{ contact.relationship || "-" }}</strong>
                    </div>
                    <div class="tk-contact-row">
                        <span>Phone:</span>
                        <div>
                            <a v-if="contact.phone" :href="`tel:${contact.phone}`">{{ contact.phone }}</a>
                            <span v-else class="text-grey-7">-</span>
                            <q-badge
                                v-if="!contact.permission_to_contact"
                                label="Do Not Contact"
                                dense
                                outline
                                color="warning"
                                class="q-ml-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div v-else class="text-grey-7">No emergency contacts recorded.</div>
        </section>

        <section class="tk-card">
            <div class="tk-card-header">
                <h2>Client Details</h2>
                <div class="row items-center q-gutter-sm">
                    <q-btn
                        flat
                        dense
                        no-caps
                        color="primary"
                        label="Change status"
                        v-if="canEditStatus"
                        @click="$emit('edit-status')"
                    />
                    <q-btn
                        flat
                        dense
                        no-caps
                        color="primary"
                        label="Edit client engagement"
                        @click="$emit('edit-client')"
                    />
                </div>
            </div>
            <div class="tk-field-grid">
                <div v-for="field in clientDetailFields" :key="field.label" class="tk-field-row">
                    <span>{{ field.label }}</span>
                    <strong>{{ field.value }}</strong>
                </div>
            </div>
        </section>

        <section class="tk-card">
            <ReferralEngagementInformation :engagement="engagement">
                <template #actions>
                    <q-btn
                        flat
                        dense
                        no-caps
                        color="primary"
                        label="Edit"
                        @click="$emit('edit-client')"
                    />
                </template>
            </ReferralEngagementInformation>
        </section>

        <TkEngagementCustomFieldsPanel
            :person-id="engagement.tk_person_id"
            :engagement-id="engagement.id"
        />
    </section>
</template>

<script>
import ReferralEngagementInformation from "@/components/TkClients/ReferralEngagementInformation.vue";
import TkEngagementCustomFieldsPanel from "@/components/TkClients/TkEngagementCustomFieldsPanel.vue";
import {
    personContactRows,
    personEmergencyContacts,
    personFromEngagement,
    personFullName,
} from "@/services/util/tkClients";

export default {
    name: "TkEngagementInformationPanel",
    components: {
        ReferralEngagementInformation,
        TkEngagementCustomFieldsPanel,
    },
    emits: ["edit-client", "edit-person", "edit-status"],
    props: {
        canEditStatus: { type: Boolean, default: false },
        engagement: {
            type: Object,
            required: true,
        },
    },
    computed: {
        person() {
            return personFromEngagement(this.engagement);
        },
        fullName() {
            const name = personFullName(this.engagement);
            return name || this.engagement.personName || "-";
        },
        personLocation() {
            const parts = [
                this.person.address_line_1,
                this.person.suburb,
                this.person.city,
                this.person.postcode,
            ].filter(Boolean);
            return parts.length ? parts.join(", ") : "-";
        },
        personDetailFields() {
            return [
                { label: "Full name", value: this.fullName },
                { label: "Preferred name", value: this.engagement.preferred_name || "-" },
                { label: "DOB", value: this.engagement.dateOfBirth },
                { label: "Gender", value: this.engagement.gender_id || "-" },
                { label: "Ethnicity", value: this.engagement.ethnicity_id || "-" },
                { label: "Iwi", value: this.engagement.iwi_id || "-" },
                { label: "NHI", value: this.engagement.nhi || "-" },
                { label: "Address", value: this.personLocation },
            ];
        },
        clientDetailFields() {
            return [
                { label: "Service", value: this.engagement.service_name || "-" },
                { label: "Status", value: this.engagement.statusLabel },
                { label: "Needs", value: this.engagement.needsTierLabel },
                { label: "Entry date", value: this.engagement.enteredAt },
                { label: "Activation date", value: this.engagement.activatedAt },
                { label: "Exit date", value: this.engagement.exitedAt },
                { label: "Exit reason", value: this.engagement.exitReason },
                { label: "Referred to", value: this.engagement.referredTo },
                { label: "Signed off by", value: this.engagement.signedOffByName },
                { label: "Sign-off date", value: this.engagement.signedOffAt },
                { label: "Primary worker", value: this.engagement.assignedWorkerName },
                { label: "Additional workers", value: this.additionalWorkerNames },
                { label: "Risk assessment", value: this.riskAssessmentLabels || "None" },
                { label: "Risk assessment date", value: this.engagement.riskAssessmentAt },
                { label: "Risk assessment notes", value: this.engagement.risk_assessment_notes || "-" },
                { label: "Active reminder last reviewed", value: this.engagement.activeReminderReviewedAt },
            ];
        },
        additionalWorkerNames() {
            const names = this.engagement.additionalWorkers.map(worker => worker.workerName);
            return names.length ? names.join(", ") : "None";
        },
        riskAssessmentLabels() {
            return this.engagement.risk_assessments.map(risk => risk.label).join(", ");
        },
        emergencyContacts() {
            return personEmergencyContacts(this.engagement);
        },
        contactRows() {
            return personContactRows(this.engagement);
        },
    },
};
</script>

<style scoped>
.tk-client-information {
    display: grid;
    gap: 12px;
    padding: 16px 24px 28px;
}

.tk-information-subheading {
    margin: 0 0 12px;
    color: #46544e;
    font-size: 0.9rem;
    font-weight: 600;
    letter-spacing: 0.025em;
    line-height: 1.25;
}

.tk-emergency-contact + .tk-emergency-contact {
    padding-top: 10px;
    border-top: 1px solid rgba(47, 62, 70, 0.06);
}

@media (max-width: 760px) {
    .tk-client-information {
        padding: 12px;
    }
}
</style>
